import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    ReloadOutlined,
    DollarOutlined,
    InfoCircleOutlined,
    CalendarOutlined,
    ShopOutlined,
    EyeOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    ShoppingCartOutlined,
    UserOutlined,
    PercentageOutlined,
    WalletOutlined
} from "@ant-design/icons";
import { Tag, Modal, Button, Col, Select, Typography, Row, Table, Card } from "antd";
import Breadcrumbs from "../../component/Breadcrumbs";
import PayoutServ from "../../service/PayoutServ";
import ShopServ from "../../service/ShopServ";
import TableActions from "../../component/ui/TableActions";
import { FilterBar, SecondaryFilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import { ResetButton } from "../../component/ui/ActionButtons";
import DataTable from "../../component/ui/DataTable";
import { usePreference } from "../../context/PreferenceContext";
import dayjs from "dayjs";

const { Text, Title } = Typography;
const { Option } = Select;

const SellerEarningsTransfers = () => {
    const { formatPrice } = usePreference();
    const navigate = useNavigate();

    const [orders, setOrders] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [paymentMethodFilter, setPaymentMethodFilter] = useState("");

    const [shops, setShops] = useState([]);
    const [shopFilter, setShopFilter] = useState("");
    const [yearFilter, setYearFilter] = useState("");
    const [monthFilter, setMonthFilter] = useState("");

    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchShops = async () => {
        try {
            const res = await ShopServ.getSellerShops();
            const shopList = Array.isArray(res) ? res : (res && Array.isArray(res.data) ? res.data : []);
            setShops(shopList);
        } catch (error) {
            console.error("Error fetching own shops:", error);
        }
    };

    const fetchPayouts = async () => {
        try {
            setLoading(true);
            const params = {};
            if (statusFilter) params.transferStatus = statusFilter;
            if (paymentMethodFilter) params.paymentMethod = paymentMethodFilter;
            if (searchText) params.search = searchText;

            const res = await PayoutServ.getSellerOwnPayouts(params);
            const orderList = Array.isArray(res) ? res : (res && Array.isArray(res.data) ? res.data : []);
            const statsData = res && res.stats ? res.stats : null;

            setOrders(orderList);
            setStats(statsData);
        } catch (error) {
            console.error("Error fetching own payouts:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchShops();
    }, []);

    useEffect(() => {
        fetchPayouts();
    }, [statusFilter, paymentMethodFilter, searchText]);

    const handleRowClick = (record) => {
        setSelectedRecord(record);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedRecord(null);
    };

    // Calculate seller earnings helper
    const getOrderTotals = (items = []) => {
        return items.reduce((acc, item) => {
            const qty = item.quantity || 1;
            acc.mrp += (item.mrp || item.price || 0) * qty;
            acc.sellerPrice += (item.sellerPrice || item.price || 0) * qty;
            return acc;
        }, { mrp: 0, sellerPrice: 0 });
    };

    const filteredOrders = orders.filter(order => {
        // Shop filter
        if (shopFilter) {
            const sId = order.shopId?._id || order.shopId || order.shop?._id || order.shop;
            if (sId !== shopFilter) return false;
        }

        // Delivered Date
        const dDate = order.deliveredAt ||
            order.statusHistory?.find(h => h.status === 'delivered')?.timestamp ||
            order.updatedAt;

        if (!dDate) {
            if (yearFilter || monthFilter) return false;
            return true;
        }

        const orderDate = dayjs(dDate);

        // Year filter
        if (yearFilter) {
            if (orderDate.year().toString() !== yearFilter) return false;
        }

        // Month filter
        if (monthFilter) {
            if (orderDate.month().toString() !== monthFilter) return false;
        }

        return true;
    });

    const getStatsForFiltered = () => {
        let totalEarnings = 0;
        let pendingTransfers = 0;
        let completedTransfers = 0;

        filteredOrders.forEach(order => {
            const totals = getOrderTotals(order.items);
            const sellerEarn = totals.sellerPrice - (order.couponDiscount || 0);

            totalEarnings += sellerEarn;

            const isPaid = order.sellerTransferStatus === "TRANSFERRED" || order.sellerPayoutStatus === "PAID_OUT";
            if (isPaid) {
                completedTransfers += sellerEarn;
            } else {
                pendingTransfers += sellerEarn;
            }
        });

        return {
            totalEarnings,
            pendingTransfers,
            completedTransfers
        };
    };

    const currentStats = getStatsForFiltered();

    const uniqueYears = Array.from(new Set(orders.map(o => {
        const d = o.deliveredAt || o.statusHistory?.find(h => h.status === 'delivered')?.timestamp || o.updatedAt;
        return d ? dayjs(d).year().toString() : null;
    }).filter(Boolean))).sort((a, b) => b.localeCompare(a));

    const columns = [
        {
            title: "Order ID",
            key: "orderId",
            render: (_, record) => (
                <div onClick={() => handleRowClick(record)} style={{ cursor: 'pointer' }}>
                    <span style={{ fontWeight: 800, color: '#4776E6', fontSize: '13px', textDecoration: 'underline' }}>
                        #{record.orderId}
                    </span>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                        {record.items?.length || 0} item{record.items?.length !== 1 ? 's' : ''}
                    </div>
                </div>
            )
        },
        {
            title: "Customer Name",
            key: "customer",
            render: (_, record) => {
                const name = record.user ? `${record.user.firstName || ''} ${record.user.lastName || ''}`.trim() : 'Guest Customer';
                return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: 500, color: '#1e293b', fontSize: '13px' }}>
                            {name}
                        </span>
                    </div>
                );
            }
        },
        {
            title: "Shop Name",
            key: "shop",
            render: (_, record) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <ShopOutlined style={{ color: '#64748b' }} />
                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 500 }}>
                        {record.shopId?.name || "—"}
                    </span>
                </div>
            )
        },
        {
            title: "Seller Final Earnings",
            key: "sellerEarnings",
            align: "right",
            render: (_, record) => {
                const totals = getOrderTotals(record.items);
                const sellerEarn = totals.sellerPrice - (record.couponDiscount || 0);
                return (
                    <span style={{ color: '#10b981', fontWeight: 600, fontSize: '13px' }}>
                        {formatPrice(sellerEarn)}
                    </span>
                );
            }
        },
        {
            title: "Delivered Date",
            key: "deliveredDate",
            render: (_, record) => {
                const deliveredDate = record.deliveredAt ||
                    record.statusHistory?.find(h => h.status === 'delivered')?.timestamp ||
                    record.updatedAt;
                return (
                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                        {deliveredDate ? dayjs(deliveredDate).format('DD MMM YYYY') : "—"}
                    </span>
                );
            }
        },
        {
            title: "Payment Method",
            key: "paymentMethod",
            align: 'center',
            render: (_, record) => {
                const { formatPaymentDisplay } = require('../../util/paymentFormatter');
                const method = formatPaymentDisplay(
                    record.paymentMethod || record.paymentType || record.deliveryPaymentMethod || "COD",
                    record.paymentCollectionMethod || record.paymentMethod || record.paymentType,
                    record.paymentSubMethod
                );
                return (
                    <Tag
                        color={method.startsWith('COD') && !method.includes('ONLINE') ? "blue" : "purple"}
                        style={{ fontSize: '11px', fontWeight: 700, borderRadius: '8px', padding: '2px 8px', textTransform: 'uppercase' }}
                    >
                        {method.toUpperCase()}
                    </Tag>
                );
            }
        },
        {
            title: "Transfer Status",
            key: "transferStatus",
            align: 'center',
            render: (_, record) => {
                const isPaid = record.sellerTransferStatus === "TRANSFERRED" || record.sellerPayoutStatus === "PAID_OUT";
                return (
                    <Tag
                        color={isPaid ? "success" : "warning"}
                        style={{ fontSize: '11px', fontWeight: 700, borderRadius: '8px', padding: '2px 8px', textTransform: 'uppercase' }}
                    >
                        {isPaid ? "Paid" : "Pending"}
                    </Tag>
                );
            }
        },
        {
            title: "Transfer Date",
            key: "transferDate",
            render: (_, record) => {
                const tDate = record.sellerTransferDate || record.sellerPayoutDate;
                return (
                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                        {tDate ? dayjs(tDate).format('DD MMM YYYY, hh:mm A') : "—"}
                    </span>
                );
            }
        },

        {
            title: "Action",
            key: "action",
            align: "center",
            width: 100,
            render: (_, record) => (
                <Button
                    type="primary"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(record);
                    }}
                    style={{
                        borderRadius: 3,
                        fontWeight: 600,
                        background: '#ffffffff',
                        color: '#004754ff',
                        border: '1px solid #e4e4e7',
                        boxShadow: '0 0 0 1px #e4e4e7',
                        height: 'auto',
                        padding: '0px 8px',
                        fontSize: 13,
                    }}
                >
                    View
                </Button>
            )
        }
    ];

    const fetchAllPayouts = async () => {
        return filteredOrders;
    };

    return (
        <div className="pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs items={[{ title: "Seller" }, { title: "Earnings & Transfers" }]} />
            </div>

            <div className="sd-container" style={{ padding: '24px', background: 'white', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                {/* Header Section */}
                <div className="sd-header" style={{ marginBottom: '24px', background: 'transparent', padding: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        <div className="sd-orders-icon-wrap" style={{ width: '48px', height: '48px', background: '#fff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                            <DollarOutlined style={{ fontSize: '22px', color: '#10b981' }} />
                        </div>
                        <div>
                            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' }}>Earnings & Transfers</h1>
                            <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', fontWeight: 500 }}>Track your sales, platform commission, and transparent payouts</p>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <TableActions
                            onFetchFullData={fetchAllPayouts}
                            columns={columns}
                            fileName="seller_earnings_ledger"
                            pageName="Seller Earnings and Transfers"
                        />
                        <ResetButton
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                setSearchText("");
                                setStatusFilter("");
                                setPaymentMethodFilter("");
                                setShopFilter("");
                                setYearFilter("");
                                setMonthFilter("");
                                fetchPayouts();
                            }}
                            text="Refresh"
                        />
                    </div>
                </div>

                {/* 6 TOP SUMMARY STATS SECTION */}
                <Row gutter={[16, 16]} className="pcu-margin-bottom-24" style={{ marginBottom: '24px' }}>


                    {/* Card 2: Total Earnings */}
                    <Col xs={12} sm={8} md={8}>
                        <div style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            padding: '14px 16px',
                            borderRadius: '12px',
                            color: '#fff',
                            boxShadow: '0 4px 10px rgba(16, 185, 129, 0.15)',
                            height: '80px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.9 }}>
                                Total Earnings
                            </span>
                            <span style={{ fontSize: '18px', fontWeight: 800, marginTop: '4px' }}>
                                {formatPrice(currentStats.totalEarnings)}
                            </span>
                        </div>
                    </Col>

                    {/* Card 3: Pending Transfers */}
                    <Col xs={12} sm={8} md={8}>
                        <div style={{
                            background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                            padding: '14px 16px',
                            borderRadius: '12px',
                            color: '#fff',
                            boxShadow: '0 4px 10px rgba(249, 115, 22, 0.15)',
                            height: '80px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.9 }}>
                                Pending
                            </span>
                            <span style={{ fontSize: '18px', fontWeight: 800, marginTop: '4px' }}>
                                {formatPrice(currentStats.pendingTransfers)}
                            </span>
                        </div>
                    </Col>

                    {/* Card 4: Completed Transfers */}
                    <Col xs={12} sm={8} md={8}>
                        <div style={{
                            background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                            padding: '14px 16px',
                            borderRadius: '12px',
                            color: '#fff',
                            boxShadow: '0 4px 10px rgba(139, 92, 246, 0.15)',
                            height: '80px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.9 }}>
                                Completed
                            </span>
                            <span style={{ fontSize: '18px', fontWeight: 800, marginTop: '4px' }}>
                                {formatPrice(currentStats.completedTransfers)}
                            </span>
                        </div>
                    </Col>
                </Row>

                {/* Filter Bar */}
                <SecondaryFilterBar>
                    {/* 1. Search */}
                    <Col xs={24} sm={12} md={5}>
                        <SearchInput
                            label="Search Order ID"
                            placeholder="Search Order ID..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </Col>

                    {/* 5. Transfer Status */}
                    <Col xs={24} sm={8} md={4}>
                        <div>
                            <div className="pcu-font-11 pcu-font-700 pcu-margin-bottom-6 pcu-opacity-50 pcu-uppercase" style={{ fontSize: '11px', fontWeight: 700, marginBottom: '6px', opacity: 0.5 }}>
                                Transfer Status
                            </div>
                            <Select
                                placeholder="All Statuses"
                                value={statusFilter}
                                onChange={(val) => setStatusFilter(val)}
                                style={{ width: '100%', height: '34px' }}
                            >
                                <Option value="">All Statuses</Option>
                                <Option value="NOT_TRANSFERRED">Pending</Option>
                                <Option value="TRANSFERRED">Transferred</Option>
                            </Select>
                        </div>
                    </Col>

                    {/* 6. Payment Method */}
                    <Col xs={24} sm={12} md={5}>
                        <div>
                            <div className="pcu-font-11 pcu-font-700 pcu-margin-bottom-6 pcu-opacity-50 pcu-uppercase" style={{ fontSize: '11px', fontWeight: 700, marginBottom: '6px', opacity: 0.5 }}>
                                Payment Method
                            </div>
                            <Select
                                placeholder="All Methods"
                                value={paymentMethodFilter}
                                onChange={(val) => setPaymentMethodFilter(val)}
                                style={{ width: '100%', height: '34px' }}
                            >
                                <Option value="">All Methods</Option>
                                <Option value="COD">COD</Option>
                                <Option value="ONLINE">ONLINE</Option>
                                <Option value="PREPAID">PREPAID</Option>
                            </Select>
                        </div>
                    </Col>

                    {/* 2. Shop Filter */}
                    <Col xs={24} sm={12} md={4}>
                        <div>
                            <div className="pcu-font-11 pcu-font-700 pcu-margin-bottom-6 pcu-opacity-50 pcu-uppercase" style={{ fontSize: '11px', fontWeight: 700, marginBottom: '6px', opacity: 0.5 }}>
                                Shop Filter
                            </div>
                            <Select
                                placeholder="All Shops"
                                value={shopFilter}
                                onChange={(val) => setShopFilter(val)}
                                style={{ width: '100%', height: '34px' }}
                            >
                                <Option value="">All Shops</Option>
                                {shops.map(shop => (
                                    <Option key={shop._id} value={shop._id}>{shop.name}</Option>
                                ))}
                            </Select>
                        </div>
                    </Col>

                    {/* 3. Year */}
                    <Col xs={24} sm={8} md={3}>
                        <div>
                            <div className="pcu-font-11 pcu-font-700 pcu-margin-bottom-6 pcu-opacity-50 pcu-uppercase" style={{ fontSize: '11px', fontWeight: 700, marginBottom: '6px', opacity: 0.5 }}>
                                Year
                            </div>
                            <Select
                                placeholder="All Years"
                                value={yearFilter}
                                onChange={(val) => setYearFilter(val)}
                                style={{ width: '100%', height: '34px' }}
                            >
                                <Option value="">All Years</Option>
                                {uniqueYears.map(year => (
                                    <Option key={year} value={year}>{year}</Option>
                                ))}
                            </Select>
                        </div>
                    </Col>

                    {/* 4. Month */}
                    <Col xs={24} sm={8} md={3}>
                        <div>
                            <div className="pcu-font-11 pcu-font-700 pcu-margin-bottom-6 pcu-opacity-50 pcu-uppercase" style={{ fontSize: '11px', fontWeight: 700, marginBottom: '6px', opacity: 0.5 }}>
                                Month
                            </div>
                            <Select
                                placeholder="All Months"
                                value={monthFilter}
                                onChange={(val) => setMonthFilter(val)}
                                style={{ width: '100%', height: '34px' }}
                            >
                                <Option value="">All Months</Option>
                                <Option value="0">January</Option>
                                <Option value="1">February</Option>
                                <Option value="2">March</Option>
                                <Option value="3">April</Option>
                                <Option value="4">May</Option>
                                <Option value="5">June</Option>
                                <Option value="6">July</Option>
                                <Option value="7">August</Option>
                                <Option value="8">September</Option>
                                <Option value="9">October</Option>
                                <Option value="10">November</Option>
                                <Option value="11">December</Option>
                            </Select>
                        </div>
                    </Col>

                </SecondaryFilterBar>

                {/* DataTable */}
                <div style={{ marginTop: '20px' }}>
                    <DataTable
                        loading={loading}
                        columns={columns}
                        data={filteredOrders}
                        rowKey="_id"
                        pagination={{
                            pageSize: 10,
                            onChange: (page) => {
                                const pageContent = document.querySelector('.page-content');
                                if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
                                else window.scrollTo({ top: 0, behavior: 'smooth' });
                            }
                        }}
                    />
                </div>
            </div>

            {/* HIGH-FIDELITY transparent ledgers MODAL */}
            {selectedRecord && (
                <Modal
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '4px 0' }}>
                            <div style={{
                                width: '48px',
                                height: '48px',
                                backgroundColor: '#f5f3ff',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(124, 58, 237, 0.08)',
                                border: '1px solid #ddd6fe'
                            }}>
                                <DollarOutlined style={{ fontSize: '22px', color: '#7c3aed' }} />
                            </div>
                            <div>
                                <div style={{ fontSize: '18px', fontWeight: 800, color: '#1e293b' }}>
                                    Order {selectedRecord.orderId}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                                        Customer: {selectedRecord.user ? `${selectedRecord.user.firstName || ''} ${selectedRecord.user.lastName || ''}`.trim() : 'Guest Customer'}
                                    </span>
                                    <Tag color="blue" style={{ fontSize: '10px', fontWeight: 700, borderRadius: '6px', margin: 0, padding: '1px 6px', textTransform: 'uppercase' }}>
                                        {selectedRecord.status?.toUpperCase() || 'ORDERED'}
                                    </Tag>
                                    <Tag color="cyan" style={{ fontSize: '10px', fontWeight: 700, borderRadius: '6px', margin: 0, padding: '1px 6px', textTransform: 'uppercase' }}>
                                        {selectedRecord.paymentMethod || selectedRecord.paymentType || selectedRecord.deliveryPaymentMethod || "COD"}
                                    </Tag>
                                    {selectedRecord.paymentCollectionMethod && (
                                        <Tag color="purple" style={{ fontSize: '10px', fontWeight: 700, borderRadius: '6px', margin: 0, padding: '1px 6px', textTransform: 'uppercase' }}>
                                            COLLECTED VIA {selectedRecord.paymentCollectionMethod}
                                        </Tag>
                                    )}
                                </div>
                                {selectedRecord.transactionId && (
                                    <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 500, marginTop: '4px' }}>
                                        Txn ID: {selectedRecord.transactionId}
                                    </div>
                                )}
                            </div>
                        </div>
                    }
                    open={isModalOpen}
                    onCancel={handleCloseModal}
                    centered
                    footer={[
                        <Button key="close" type="default" onClick={handleCloseModal} style={{ borderRadius: '8px', fontWeight: 600, padding: '4px 20px', height: '38px' }}>
                            Close
                        </Button>
                    ]}
                    width={800}
                    bodyStyle={{ padding: '16px 24px' }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {/* Status Card & Overview */}
                        <div style={{
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: '10px',
                            padding: '8px 16px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <div>
                                <span style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.5px' }}>Transfer Status</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                                    {selectedRecord.sellerTransferStatus === "TRANSFERRED" || selectedRecord.sellerPayoutStatus === "PAID_OUT" ? (
                                        <>
                                            <CheckCircleOutlined style={{ color: '#10b981', fontSize: '13px' }} />
                                            <span style={{ fontSize: '12px', fontWeight: 700, color: '#10b981' }}>Transferred Successfully</span>
                                        </>
                                    ) : (
                                        <>
                                            <ClockCircleOutlined style={{ color: '#f59e0b', fontSize: '13px' }} />
                                            <span style={{ fontSize: '12px', fontWeight: 700, color: '#f59e0b' }}>Pending Transfer</span>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <span style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.5px' }}>Delivered Date</span>
                                <div style={{ fontSize: '12px', fontWeight: 700, color: '#334155', marginTop: '2px' }}>
                                    {dayjs(selectedRecord.deliveredAt || selectedRecord.statusHistory?.find(h => h.status === 'delivered')?.timestamp || selectedRecord.updatedAt).format('DD MMM YYYY, hh:mm A')}
                                </div>
                            </div>
                        </div>

                        {/* ROW 1: Shop Details & Customer Details */}
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <h3 style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <ShopOutlined /> Shop Details
                                    </h3>
                                    <div
                                        onClick={() => navigate("/seller/shop-setup")}
                                        style={{
                                            background: '#fff',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '12px',
                                            padding: '10px 12px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '8px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = '#7c3aed';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(124, 58, 237, 0.08)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = '#e2e8f0';
                                            e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.02)';
                                        }}
                                    >
                                        <div>
                                            <span style={{ fontSize: '9px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Shop Name</span>
                                            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginTop: '2px' }}>
                                                {selectedRecord.shopId?.name || '—'}
                                            </div>
                                        </div>
                                        <div>
                                            <span style={{ fontSize: '9px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Shop ID</span>
                                            <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginTop: '2px', fontFamily: 'monospace' }}>
                                                {selectedRecord.shopId?.shopId || selectedRecord.shopId?._id?.toString?.().slice(-8).toUpperCase() || '—'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>

                            <Col span={12}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <h3 style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <UserOutlined /> Customer Details
                                    </h3>
                                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                            <div>
                                                <span style={{ fontSize: '9px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Customer Name</span>
                                                <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginTop: '2px' }}>
                                                    {selectedRecord.user ? `${selectedRecord.user.firstName || ''} ${selectedRecord.user.lastName || ''}`.trim() : 'Guest Customer'}
                                                </div>
                                            </div>
                                            <div>
                                                <span style={{ fontSize: '9px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Phone Contact</span>
                                                <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', marginTop: '2px' }}>
                                                    {selectedRecord.user?.phone || '—'}
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <span style={{ fontSize: '9px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Delivery Address</span>
                                            <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginTop: '2px', lineHeight: '1.4' }}>
                                                {selectedRecord.deliveryAddress ? `${selectedRecord.deliveryAddress.street || ''}, ${selectedRecord.deliveryAddress.city || ''}, ${selectedRecord.deliveryAddress.district || ''} - ${selectedRecord.deliveryAddress.pincode || ''}` : 'No address provided'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>

                        {/* ROW 2: Products Sold & Transparent Accounting Breakdown */}
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <h3 style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <ShoppingCartOutlined /> Products Sold
                                    </h3>
                                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                                    <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '9px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>Item</th>
                                                    <th style={{ textAlign: 'center', padding: '8px 12px', fontSize: '9px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', width: '40px' }}>Qty</th>
                                                    <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '9px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', width: '80px' }}>Seller P.</th>
                                                    <th style={{ textAlign: 'right', padding: '8px 12px', fontSize: '9px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', width: '80px' }}>Cust P.</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(selectedRecord.items || []).map((item, idx) => (
                                                    <tr key={idx} style={{ borderBottom: idx < (selectedRecord.items.length - 1) ? '1px solid #f1f5f9' : 'none' }}>
                                                        <td style={{ padding: '8px 12px', fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                                                            {item.name}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', textAlign: 'center', fontSize: '12px', color: '#475569' }}>
                                                            {item.quantity}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#10b981' }}>
                                                            {formatPrice(item.sellerPrice || item.price || 0)}
                                                        </td>
                                                        <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: '12px', fontWeight: 500, color: '#334155' }}>
                                                            {formatPrice(item.price || 0)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </Col>

                            <Col span={12}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <h3 style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#475569', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <PercentageOutlined /> Accounting Breakdown
                                    </h3>
                                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                            <span style={{ color: '#64748b', fontWeight: 500 }}>Seller Base Earnings</span>
                                            <span style={{ fontWeight: 600, color: '#0f766e' }}>{formatPrice(getOrderTotals(selectedRecord.items).sellerPrice)}</span>
                                        </div>
                                        {selectedRecord.couponDiscount > 0 && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px' }}>
                                                <span style={{ color: '#e11d48', fontWeight: 500 }}>Coupon Discount Applied</span>
                                                <span style={{ fontWeight: 600, color: '#e11d48' }}>- {formatPrice(selectedRecord.couponDiscount)}</span>
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', background: '#f0fdf4', padding: '8px 12px', borderRadius: '8px', border: '1px solid #bbf7d0', marginTop: '4px' }}>
                                            <span style={{ color: '#166534', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <WalletOutlined /> Net Transferred Earnings
                                            </span>
                                            <span style={{ fontWeight: 800, color: '#166534' }}>
                                                {formatPrice(getOrderTotals(selectedRecord.items).sellerPrice - (selectedRecord.couponDiscount || 0))}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default SellerEarningsTransfers;
